import { Box, Typography, Button as MuiButton } from '@mui/material'
import Button from 'components/buttons/Button'
import Popup from 'components/popup/Popup'
import PopupSection from 'components/popup/PopupSection'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { userLang } from 'utils/lang'
import { extractCountryCode } from 'utils/phone'

interface AddMemberByPhonePopupProps {
  children: ReactNode
  open: boolean
  setOpen: (open: boolean) => void
  onAddMember: (phone: string) => void
}

export function AddMemberByPhonePopupContent(props: {
  onAddMember: (phone: string) => void
  setOpen: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [phone, setPhone] = useState('')
  const [country] = useState<string | undefined>(userLang())
  const phoneInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (phone.length === 0)
      setTimeout(() => {
        phoneInputRef.current?.focus()
      }, 100)
  }, [phone])

  const handleAdd = () => {
    if (isValidPhoneNumber(`+${phone}`)) {
      props.onAddMember(`+${phone}`)

      // keep previous phone number country code for next add
      const countryCode = extractCountryCode(`+${phone}`)
      setPhone(countryCode.slice(1)) // Remove + as PhoneInput handles it

      setTimeout(() => {
        phoneInputRef.current?.focus()
      }, 100)
    }
  }

  // on enter click, add member in case phone number is valid
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleAdd()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [phone])

  return (
    <Box
      sx={{
        direction: 'ltr',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%'
      }}
    >
      <Box
        sx={{
          direction: 'ltr',
          display: 'flex',
          flex: 1,
          minWidth: 0,

          '& .react-tel-input .selected-flag:hover, & .react-tel-input .selected-flag.open':
            {
              backgroundColor: '#F0F0F0'
            },
          '& .react-tel-input .selected-flag': {
            backgroundColor: '#F0F0F0',
            borderRight: '1px solid #E0E0E0',

            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px'
          },
          '& .react-tel-input .flag-dropdown:after': {
            borderTop: '4px solid #666',
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent'
          }
        }}
      >
        <PhoneInput
          enableSearch
          placeholder={t('phoneNumberPlaceholder', 'Phone number')}
          inputProps={{ ref: phoneInputRef }}
          inputStyle={{
            direction: 'ltr',
            width: '100%',
            height: '30px',
            borderRadius: '8px',
            border: '1px solid #E0E0E0',
            fontSize: '14px',
            paddingLeft: '48px',
            boxSizing: 'border-box'
          }}
          buttonStyle={{
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
            border: 'none',

            backgroundColor: '#F0F0F0',

            cursor: 'pointer'
          }}
          containerStyle={{
            width: '100%'
          }}
          dropdownStyle={{
            width: '300px',
            borderRadius: '8px'
          }}
          country={country}
          value={phone}
          onChange={(value) => setPhone(value)}
        />
      </Box>

      <MuiButton
        variant="contained"
        onClick={handleAdd}
        disabled={!isValidPhoneNumber(`+${phone}`)}
        sx={{
          height: '30px',
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 500,
          padding: '0px',
          flexShrink: 0,
          minWidth: '48px',
          borderRadius: '0 8px 8px 0',
          marginLeft: '-8px',
          zIndex: 1,
          backgroundColor: '#0F7FEB',
          '&:hover': {
            backgroundColor: '#0A5FA1'
          },
          '&:disabled': {
            backgroundColor: '#B0B0B0'
          }
        }}
      >
        {t('add', 'Add')}
      </MuiButton>
    </Box>
  )
}

export default function AddMemberByPhonePopup(
  props: AddMemberByPhonePopupProps
) {
  return (
    <Popup
      padding={0}
      closeOnTouchOutside
      open={props.open}
      setOpen={props.setOpen}
      placement="left"
      content={
        <AddMemberByPhonePopupContent
          onAddMember={props.onAddMember}
          setOpen={props.setOpen}
        />
      }
    >
      {props.children}
    </Popup>
  )
}
